import React, { useEffect, useState } from "react";
import { t as tr } from "../i18n";

const API = process.env.REACT_APP_API_URL || "";

// « Photo_du_film-final.jpg » → « Photo du film final » : un titre de départ,
// à corriger au besoin.
function titleFromFile(name) {
  return name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
}

// Ajout en lot dans un thème : des mots (Pictionnary, un par ligne), des
// paires d'extrêmes (Hémicycle, « Froid / Chaud » par ligne) ou des
// images avec leur titre (Pixel Guess / Références, Smash or Pass). Les images
// ne partent qu'à la validation : annuler ne laisse rien sur le serveur.
export default function AdminBatchAdd({ theme, contentType, authFetch, flash, onClose, onDone }) {
  const isPairs = contentType === "pairs";
  const isWords = contentType === "words" || isPairs;
  const isCards = contentType === "cards";
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);   // { file, url, title, subtitle }
  const [busy, setBusy] = useState("");

  // Les aperçus locaux (blob:) se libèrent en partant.
  useEffect(() => () => files.forEach(f => URL.revokeObjectURL(f.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const words = isWords
    ? [...new Set(text.split(/\n/).map(w => w.trim()).filter(Boolean))]
      // Une paire bancale (pas exactement deux côtés) est ignorée
      .filter(w => !isPairs || /^[^/]*\S[^/]*\/[^/]*\S[^/]*$/.test(w))
    : [];

  const addFiles = (list) => {
    const picked = [...list].filter(f => /^image\//.test(f.type)).map(file => ({
      file, url: URL.createObjectURL(file), title: titleFromFile(file.name), subtitle: "",
    }));
    setFiles(prev => prev.concat(picked));
  };
  const edit = (i, field, value) => setFiles(prev => prev.map((f, j) => (j === i ? { ...f, [field]: value } : f)));
  const remove = (i) => setFiles(prev => { URL.revokeObjectURL(prev[i].url); return prev.filter((_, j) => j !== i); });

  const submit = async () => {
    let items;
    if (isWords) {
      items = words.map(w => ({ question: w, answers: [], correct_answer: 0, difficulty: 1, type: "none", format: "classic" }));
    } else {
      const ready = files.filter(f => f.title.trim());
      items = [];
      for (const [i, f] of ready.entries()) {
        setBusy(tr("admin.batchUploading", { n: i + 1, total: ready.length }));
        const fd = new FormData();
        fd.append("file", f.file);
        const r = await authFetch(`${API}/admin/upload`, { method: "POST", body: fd });
        const d = await r?.json().catch(() => ({}));
        if (!r?.ok || !d.url) { setBusy(""); return flash(`${f.file.name} : ${d?.error || tr("admin.error")}`, true); }
        items.push(isCards
          // Smash or Pass : le titre est la carte, le sous-titre est facultatif.
          ? { question: f.title.trim(), short_title: f.subtitle.trim() || null, answers: [], correct_answer: 0, difficulty: 1, type: "none", media_url: d.url, format: "classic" }
          // Pixel Guess / Références : le titre est la réponse à trouver.
          : { question: "", short_title: f.title.trim(), answers: [f.title.trim()], correct_answer: 0, difficulty: 1, type: "libre", media_url: d.url, format: "classic" });
      }
    }
    if (!items.length) return;
    setBusy(tr("admin.batchSaving"));
    const r = await authFetch(`${API}/admin/questions/batch`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme_id: theme.id, items }),
    });
    const d = await r?.json().catch(() => ({}));
    setBusy("");
    if (!r?.ok) return flash(d?.error || tr("admin.error"), true);
    flash(tr("admin.batchDone", { created: d.created, skipped: d.skipped, errors: d.errors?.length || 0 }), !!d.errors?.length);
    onDone();
  };

  const count = isWords ? words.length : files.filter(f => f.title.trim()).length;

  return (
    <div className="adm-modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="adm-modal adm-batch" onClick={e => e.stopPropagation()}>
        <h3>{tr("admin.batchTitle", { theme: theme.name })}</h3>
        {isWords ? (
          <>
            <p className="adm-inv-hint">{tr(isPairs ? "admin.batchPairsHint" : "admin.batchWordsHint")}</p>
            <textarea className="adm-input adm-batch-text" rows={12} value={text} disabled={!!busy}
              placeholder={isPairs ? (theme.quiz_type_id === 11 ? "Plage / Piscine\nChat / Chien" : "Froid / Chaud\nNul / Génial") : "chat\nparapluie\nfusée"} onChange={e => setText(e.target.value)} />
          </>
        ) : (
          <>
            <p className="adm-inv-hint">{tr(isCards ? "admin.batchCardsHint" : "admin.batchImagesHint")}</p>
            <label className="btn adm-btn-dark adm-batch-pick">
              <i className="bi bi-images"></i> {tr("admin.batchPick")}
              <input type="file" accept="image/*" multiple hidden disabled={!!busy}
                onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            </label>
            <div className="adm-batch-list">
              {files.map((f, i) => (
                <div key={f.url} className="adm-batch-row">
                  <img src={f.url} alt="" />
                  <div className="adm-batch-fields">
                    <input className="adm-settings-input" value={f.title} placeholder={tr("admin.title")} disabled={!!busy}
                      onChange={e => edit(i, "title", e.target.value)} />
                    {isCards && (
                      <input className="adm-settings-input" value={f.subtitle} placeholder={tr("admin.cardSubtitle")} disabled={!!busy}
                        onChange={e => edit(i, "subtitle", e.target.value)} />
                    )}
                  </div>
                  <button type="button" className="adm-icon-btn adm-icon-btn-danger" disabled={!!busy}
                    onClick={() => remove(i)} title={tr("admin.delete")}><i className="bi bi-trash3"></i></button>
                </div>
              ))}
            </div>
          </>
        )}
        {busy && <p className="adm-inv-hint">{busy}</p>}
        <div className="adm-modal-actions">
          <button className="btn" onClick={onClose} disabled={!!busy}>{tr("admin.cancel")}</button>
          <button className="btn btn-primary" onClick={submit} disabled={!!busy || count === 0}>
            {tr("admin.batchAdd", { n: count })}
          </button>
        </div>
      </div>
    </div>
  );
}
